import {Injectable, signal, computed} from '@angular/core';
import {Logger} from '@ccp/app/lib/logger';
import {GlobalStorage} from '@ccp/struct/global-storage.struct';

@Injectable()
export class StorageContextService {
  private readonly _globalStorage = signal<object>({} as GlobalStorage);

  public readonly isEmpty = computed(() => Object.keys(this._globalStorage()).length === 0);

  /**
   * Save key/value in context
   * example: const params = {};
   *    params['attr1'] = val1;
   *    params['attr2'] = val2;
   * @param listValues
   * @param eraseExistantKey
   */
  public add(listValues: object, eraseExistantKey = false): void {
    if (listValues) {
      const storage = this._globalStorage();
      let i = 0;

      for (const property in listValues) {
        const keyObject = String(property);
        const valueObject = String(Object.values(listValues)[i]);

        if (keyObject && valueObject) {
          if (storage[keyObject] && !eraseExistantKey) {
            Logger.warn(`Key '${keyObject}' already exist and has not been erased. New value ${valueObject} has not been saved.`);
          } else if (storage[keyObject] && eraseExistantKey) {
            Logger.warn(`Key '${keyObject}' already exist and has been erased. New value : '${valueObject}'`);
            this._globalStorage.update((store) => ({...store, [keyObject]: valueObject}));
          } else {
            this._globalStorage.update((store) => ({...store, [keyObject]: valueObject}));
          }
        }
        i++;
      }
    }
    Logger.warn('Current context contains : ', this._globalStorage());
  }

  /**
   * Get value of key in context
   * @param property
   */
  public get(property: string): string | boolean | number {
    const storage = this._globalStorage();
    if (storage[property]) {
      return storage[property];
    }
  }

  /**
   * update value by key in context
   * @param property
   * @param value
   */
  public update(property: string, value: string): boolean {
    const storage = this._globalStorage();
    if (storage[property]) {
      this._globalStorage.update((store) => ({...store, [property]: value}));
      return true;
    }
    return false;
  }

  /**
   * Remove value by key in context
   * @param property
   */
  public remove(property: string): void {
    const storage = this._globalStorage();
    if (storage[property]) {
      this._globalStorage.update((store) => {
        const newStore = {...store};
        delete newStore[property];
        return newStore;
      });
      Logger.warn(`Key '${property}' has been erased. Current context contains ${JSON.stringify(this._globalStorage())}`);
    }
  }

  /**
   * Check if key exists in storage
   */
  public has(property: string): boolean {
    return property in this._globalStorage();
  }

  /**
   * Clear all storage
   */
  public clear(): void {
    this._globalStorage.set({} as GlobalStorage);
    Logger.info('Storage context cleared');
  }

  /**
   * Get storage snapshot (for debugging)
   */
  public getSnapshot(): object {
    return {...this._globalStorage()};
  }
}
